import React, { useEffect, useRef, useState } from 'react';
import { DemiLightText, MediumText, RegularText } from '../../lib/Text';
import SvgIcon from '../SvgIcon';
import { colors } from '../../lib/colors';
import { Col } from '../../lib/utils';
import clsx from 'clsx';

type RealDropDownProps = {
    options: {
        code: string;
        value: string;
        onClick?: () => void;
        object?: any;
    }[];
    defaultLabel?: string;
    onSelect?: (selected: { code: string; value: string; object?: any }) => void;
    bgColor?: string;
    textColor?: string;
    totalText?: string;
    mr?: number;
    borderRadius?: number;
    className?: string;
    arrowColor?: string;
    isAllOption?: boolean;
    initialSelected?: string;
    selected?: { code: string; value: string; object?: any };
    textType?: 'regular' | 'medium' | 'demilight';
    selectedBgColor?: string;
    selectedTextColor?: string;
    selectedArrowColor?: string;
    totalObjectOption?: any;
    fontSize?: number;
};

const RealDropDown: React.FC<RealDropDownProps> = ({
    options,
    onSelect,
    bgColor = '#F4F5F7',
    textColor = '#3D4244',
    totalText = '전체',
    borderRadius = 7,
    mr,
    className,
    arrowColor = '#3D4244',
    isAllOption = true,
    initialSelected,
    selected,
    textType = 'regular',
    selectedBgColor = bgColor,
    selectedTextColor = textColor,
    selectedArrowColor = arrowColor,
    fontSize = 17,
}) => {
    const TextComponent = textType === 'regular' ? RegularText : textType === 'medium' ? MediumText : DemiLightText;

    const dropdownRef = useRef<HTMLDivElement>(null);

    const [isOpen, setIsOpen] = useState(false);

    // Use controlled selected state if provided, otherwise fallback to initialSelected logic
    const getSelectedOptionValue = () => {
        if (selected) {
            return selected.value;
        }

        if (initialSelected) {
            const initialOption = options?.find((o) => o.code === initialSelected);
            return initialOption?.value ?? totalText;
        }

        return isAllOption ? totalText : options[0]?.value ?? totalText;
    };

    const selectedOptionValue = getSelectedOptionValue();

    const handleSelect = (option: { code: string; value: string; object?: any }) => {
        setIsOpen(false);
        if (onSelect) {
            onSelect(option);
        }
    };
    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };
    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="relative inline-block " ref={dropdownRef}>
            <button
                onClick={() => setIsOpen((prev) => !prev)}
                className={clsx(
                    `flex flex-row items-center justify-center rounded-full px-[16px]`, // 공통 클래스
                    className // className을 마지막에 병합
                )}
                style={{
                    backgroundColor: selectedOptionValue === totalText ? bgColor : selectedBgColor,
                    borderRadius: borderRadius,
                    marginRight: mr,
                }}
            >
                <TextComponent
                    fontSize={fontSize}
                    lineHeight={24.62}
                    color={selectedOptionValue === totalText ? textColor : selectedTextColor}
                    className="mr-[12px]"
                >
                    {selectedOptionValue}
                </TextComponent>
                <SvgIcon
                    name={'SvgDownStrokeArrow'}
                    fill={colors.trans}
                    stroke={selectedOptionValue === totalText ? arrowColor : selectedArrowColor}
                    size={14}
                    strokeWidth={1.5}
                />
            </button>
            {isOpen && (
                <div className="absolute mt-[10px] flex pb-[21px] w-[190px] bg-[primary] bg-white rounded-[15px] [box-shadow:0px_4px_20px_rgba(0,0,0,0.2)] z-40">
                    <ul
                        className=" pl-[12px] pr-[12px] w-[180px] rounded-[15px]  left-0 mt-[18px] bg-white border border-gray-300  max-h-[200px] overflow-y-scroll 
                scrollbar scrollbar-w-[6px] scrollbar-thumb-[#CFCFCF] scrollbar-corner-GREY_100 scrollbar-track-rounded-full scrollbar-thumb-rounded-full scrollbar-h-[20px] "
                    >
                        {isAllOption && (
                            <li
                                onClick={() =>
                                    handleSelect({
                                        code: 'all',
                                        value: totalText,
                                        object: undefined,
                                    })
                                }
                                className={`pb-[8px] text-gray-700 cursor-pointer hover:bg-gray-100  ${
                                    selectedOptionValue === totalText ? 'bg-gray-200' : ''
                                } `}
                            >
                                <Col className="flex w-full h-full hover:bg-[#F2F4F6] rounded-[7px] pl-[8px] py-[4px] ">
                                    <RegularText
                                        fontSize={16}
                                        lineHeight={24}
                                        color={selectedOptionValue === totalText ? colors.primary : '#3D4244'}
                                    >
                                        {totalText}
                                    </RegularText>
                                </Col>
                            </li>
                        )}
                        {options.map((option) => (
                            <li
                                key={option.code}
                                onClick={() => {
                                    if (option.onClick) {
                                        option.onClick();
                                        setIsOpen(false);
                                    } else {
                                        handleSelect(option);
                                    }
                                }}
                                className={`pb-[8px] text-gray-700 cursor-pointer hover:bg-gray-100  ${
                                    selectedOptionValue === option.value ? 'bg-gray-200' : ''
                                }`}
                            >
                                <Col className="flex w-full h-full hover:bg-[#F2F4F6] rounded-[7px] px-[8px] py-[4px] ">
                                    <RegularText
                                        fontSize={16}
                                        lineHeight={24}
                                        color={selectedOptionValue === option.value ? colors.primary : '3D4244'}
                                    >
                                        {option.value}
                                    </RegularText>
                                </Col>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default RealDropDown;
